/**
 * Carte des sous-compteurs de l'Université de Bordeaux (idtype `ubordeaux`,
 * DEV-2100), refacturés au Crous de Bordeaux-Aquitaine : logique PURE de
 * l'en-tête, des onglets « Données techniques », « Mandats » et « Données
 * reçues », extraite des composants pour être testée.
 *
 * Contrat : la réponse de dapi `GET /source/informations` (homeys-api-data#258,
 * `output_model_ubordeaux.py`) — `compteur.fluide` (elec | eau | chaleur | null),
 * `compteur.seuil_progression_horaire`, et `data.<variable du fluide>.<pas>`.
 *
 * INTENTION : un client qui ouvre la carte d'un sous-compteur voit son fluide,
 * son unité et les données que l'Université a déposées, et rien d'autre.
 *
 * 1. Tout ce qui varie suit `compteur.fluide`. Un fluide `null` (pas encore de
 *    donnée, ou ambigu) donne un affichage NEUTRE : il n'est jamais déduit des
 *    variables servies.
 * 2. Aucun mandat n'est à signer : l'Université dépose elle-même les exports.
 *    L'onglet le dit en information, jamais « Erreur inconnue » ni « non signé ».
 * 3. Un champ absent s'affiche « - » ; un seuil `null` est le défaut appliqué
 *    par la collecte, dit comme tel, jamais un chiffre inventé.
 */
import { formatDate, formatNumber } from "@core/helpers/format.helper"
import type {
    BornesUBordeaux,
    FluideUBordeaux,
    InformationUBordeaux,
    LineBlock,
    LineBlockLine,
} from "@types"

type Translate = (key: string) => string

/** Libellé de l'opérateur, affiché tel quel. */
export const OPERATEUR_UBORDEAUX = "Université de Bordeaux"

/**
 * Libellés français de la carte, posés sous la clé `ubordeaux` des messages de
 * chaque composant (en-tête, données techniques, mandats, données reçues) :
 * une seule source, couverte par les tests.
 */
export const MESSAGES_UBORDEAUX = {
    operateur: OPERATEUR_UBORDEAUX,
    fluide: {
        elec: "Électricité",
        eau: "Eau",
        chaleur: "Chaleur",
        inconnu: "Fluide non déterminé",
        inconnu_tooltip:
            "Le fluide de ce sous-compteur (électricité, eau ou chaleur) sera connu à la réception de ses premières données.",
    },
    technique: {
        fluide: "Fluide",
        unite: "Unité",
        seuil: "Seuil de progression horaire",
        seuil_defaut: "Défaut de la collecte",
        seuil_tooltip:
            "Progression d'index maximale admise par heure pour ce compteur. Au-delà, l'index est écarté et l'intervalle reste sans donnée.",
        operateur: "Données transmises par",
    },
    consentement: {
        titre: "Accès aux données de consommation",
        sans_mandat: "Aucun mandat requis",
        source: "Données déposées par",
    },
    flux: {
        categorie: "Consommation",
        elec: "Électricité",
        eau: "Eau",
        chaleur: "Chaleur",
        pas: "Pas de temps",
        journalier: "Journalier",
        horaire: "Horaire",
        premiere_donnee: "Date de la première donnée",
        derniere_donnee: "Date de la dernière donnée",
        recu: "Données reçues",
        non_recu: "Aucune donnée reçue",
    },
}

/**
 * Par fluide : la variable SERVIE par dapi, son unité et l'icône de son tag.
 */
export const FLUIDES_UBORDEAUX: Record<
    FluideUBordeaux,
    { variable: string; unite: string; icone: string }
> = {
    elec: { variable: "energie_elec_tout", unite: "kWh", icone: "pi pi-bolt" },
    eau: { variable: "volume_eau_tout", unite: "m³", icone: "fal fa-droplet" },
    chaleur: {
        variable: "energie_chaleur_tout",
        unite: "kWh",
        icone: "fal fa-heat",
    },
}

/** Pas servis pour chaque variable, dans l'ordre de la carte. */
const PAS_UBORDEAUX = [
    { grain: "daily", cle: "journalier" },
    { grain: "hourly", cle: "horaire" },
] as const

/** Une borne telle que servie : chaîne ISO (dapi) ou Date (store typé). */
type Borne = string | Date

function estUneBorne(value: unknown): value is Borne {
    if (typeof value === "string") return value !== ""
    return value instanceof Date && !isNaN(value.getTime())
}

function dateOuTiret(value: unknown): string {
    return estUneBorne(value) ? formatDate(value) : "-"
}

/**
 * Fluide du compteur, tel que servi par dapi. Toute autre valeur (null, absente,
 * inconnue) rend null : le fluide n'est jamais deviné depuis `data`.
 */
export function fluideUBordeaux(
    informations: InformationUBordeaux | null | undefined
): FluideUBordeaux | null {
    const fluide = informations?.compteur?.fluide
    return fluide && fluide in FLUIDES_UBORDEAUX ? fluide : null
}

/**
 * Icône de la source : compteur électrique pour `elec`, compteur générique
 * sinon (eau et chaleur, comme SIGDU et Dalkia ; fluide inconnu).
 */
export function iconeUBordeaux(
    fluide: FluideUBordeaux | null | undefined
): "elec" | "compteur" {
    return fluide === "elec" ? "elec" : "compteur"
}

/** Tag affiché dans l'en-tête de la carte. */
export type TagUBordeaux = {
    severity: string
    label: string
    icon?: string
    tooltip?: string
}

/**
 * Tags de l'en-tête : le fluide, puis l'opérateur. Aucun tag de consentement :
 * il n'y a pas de mandat.
 */
export function buildUBordeauxTags(
    informations: InformationUBordeaux | null | undefined,
    t: Translate
): TagUBordeaux[] {
    if (!informations) return []
    const fluide = fluideUBordeaux(informations)
    const tagFluide: TagUBordeaux = fluide
        ? {
              severity: "primary",
              label: t(`ubordeaux.fluide.${fluide}`),
              icon: FLUIDES_UBORDEAUX[fluide].icone,
          }
        : {
              severity: "secondary",
              label: t("ubordeaux.fluide.inconnu"),
              tooltip: t("ubordeaux.fluide.inconnu_tooltip"),
          }
    return [
        tagFluide,
        {
            severity: "primary",
            label: t("ubordeaux.operateur"),
            icon: "pi pi-building",
        },
    ]
}

/**
 * Lignes « Données techniques » : fluide, unité, seuil de progression horaire,
 * opérateur. Liste vide quand dapi ne sert rien pour ce compteur.
 */
export function buildUBordeauxTechnicalLines(
    informations: InformationUBordeaux | null | undefined,
    t: Translate
): LineBlockLine[] {
    if (!informations) return []
    const fluide = fluideUBordeaux(informations)
    const unite = fluide ? FLUIDES_UBORDEAUX[fluide].unite : null
    const seuil = informations.compteur?.seuil_progression_horaire

    let valeurSeuil = "-"
    if (unite) {
        valeurSeuil =
            seuil == null
                ? t("ubordeaux.technique.seuil_defaut")
                : formatNumber(seuil, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 0,
                      unit: `${unite}/h`,
                  })
    }

    return [
        {
            text: t("ubordeaux.technique.fluide"),
            value: t(`ubordeaux.fluide.${fluide ?? "inconnu"}`),
        },
        { text: t("ubordeaux.technique.unite"), value: unite ?? "-" },
        {
            text: t("ubordeaux.technique.seuil"),
            value: valeurSeuil,
            tooltip: t("ubordeaux.technique.seuil_tooltip"),
        },
        {
            text: t("ubordeaux.technique.operateur"),
            value: OPERATEUR_UBORDEAUX,
        },
    ]
}

/** Bornes d'un pas d'une variable, ou null quand dapi ne le sert pas. */
function bornesDuPas(
    informations: InformationUBordeaux | null | undefined,
    variable: string,
    grain: string
): BornesUBordeaux | null {
    const bloc = (informations?.data as Record<string, unknown> | null)?.[
        variable
    ]
    if (!bloc || typeof bloc !== "object") return null
    const slot = (bloc as Record<string, unknown>)[grain]
    if (!slot || typeof slot !== "object") return null
    return slot as BornesUBordeaux
}

/**
 * Onglet « Données reçues » : un bloc par pas de la variable du fluide, qui dit
 * ce qui est détenu. Fluide inconnu : un seul bloc neutre, sans fluide.
 */
export function buildUBordeauxReceivedData(
    informations: InformationUBordeaux | null | undefined,
    t: Translate
): LineBlock[] {
    const fluide = fluideUBordeaux(informations)
    if (!fluide) {
        return [
            {
                id: "fluide_inconnu",
                title: [
                    t("ubordeaux.flux.categorie"),
                    t("ubordeaux.fluide.inconnu"),
                ],
                badge: [
                    {
                        text: t("ubordeaux.flux.non_recu"),
                        icon: "pi pi-info-circle",
                        status: "info",
                        tooltip: t("ubordeaux.fluide.inconnu_tooltip"),
                    },
                ],
                lines: [],
            },
        ]
    }

    const { variable } = FLUIDES_UBORDEAUX[fluide]
    return PAS_UBORDEAUX.map(({ grain, cle }) => {
        const bornes = bornesDuPas(informations, variable, grain)
        const recu = estUneBorne(bornes?.date_from)
        return {
            id: `${variable}.${grain}`,
            title: [
                t("ubordeaux.flux.categorie"),
                t(`ubordeaux.flux.${fluide}`),
            ],
            badge: [
                recu
                    ? {
                          text: t("ubordeaux.flux.recu"),
                          icon: "pi pi-check-circle",
                          status: "success" as const,
                      }
                    : {
                          text: t("ubordeaux.flux.non_recu"),
                          icon: "pi pi-info-circle",
                          status: "info" as const,
                      },
            ],
            lines: [
                {
                    text: t("ubordeaux.flux.pas"),
                    value: t(`ubordeaux.flux.${cle}`),
                },
                {
                    text: t("ubordeaux.flux.premiere_donnee"),
                    value: dateOuTiret(bornes?.date_from),
                },
                {
                    text: t("ubordeaux.flux.derniere_donnee"),
                    value: dateOuTiret(bornes?.date_to),
                },
            ],
        }
    })
}

/**
 * Onglet « Mandats » : aucun mandat n'est à signer, les données sont déposées
 * par l'Université. Pas de menu (ni télécharger, ni prolonger, ni révoquer).
 */
export function buildUBordeauxConsentBlock(t: Translate): LineBlock {
    return {
        title: [t("ubordeaux.consentement.titre")],
        badge: [
            {
                status: "info",
                icon: "pi pi-info-circle",
                text: t("ubordeaux.consentement.sans_mandat"),
            },
        ],
        lines: [
            {
                text: t("ubordeaux.consentement.source"),
                value: OPERATEUR_UBORDEAUX,
            },
        ],
        menu: [],
    }
}

/**
 * Vrai pour un type de compteur qui n'a pas de mandat (sous-compteur de
 * l'Université de Bordeaux) : l'onglet « Mandats » ne porte alors aucune
 * alerte de consentement, l'état servi étant toujours null.
 */
export function estSansMandat(type: string | null | undefined): boolean {
    return type === "ubordeaux"
}

/**
 * Catégorie de graphiques de la page de données (`graph.store`) pour un
 * sous-compteur, selon son fluide. Il n'existe aucun graphique `ubordeaux_*`
 * au référentiel (`ConfigurationGraph`, lu en prod le 13/10/2026) : le
 * sous-compteur lit les cartes existantes dont les jeux de données portent SA
 * variable, sans condition de visibilité :
 *  - elec → `elecstrasbourg_energie_elec_tout` (« Consommation », kWh) ;
 *  - eau → `eau_volume_eau_tout` (« Consommation d'eau », m³) ;
 *  - chaleur → `rcu_energie_chaleur_tout` et `rcu_idxenergie_chaleur_tout`.
 * Les autres cartes de la catégorie restent masquées faute de variable détenue.
 * Fluide null : aucune catégorie, donc aucun graphique.
 */
export function categorieGraphesUBordeaux(
    fluide: FluideUBordeaux | null | undefined
): "GRAPH_ELECSTRASBOURG" | "GRAPH_EAU" | "GRAPH_RCU" | null {
    switch (fluide) {
        case "elec":
            return "GRAPH_ELECSTRASBOURG"
        case "eau":
            return "GRAPH_EAU"
        case "chaleur":
            return "GRAPH_RCU"
        default:
            return null
    }
}

/**
 * Les variables DÉTENUES par un sous-compteur, telles que la page de données
 * les attend pour choisir ses graphiques (`sourceStore.getVariablesBySourceId`).
 *
 * Lit `data._variables` (contrat dapi #258 : la variable du fluide et son index
 * `idx<variable>`) ; tant que dapi sert `_variables: null`, se rabat sur les
 * bornes de `data.<variable>.<pas>`, comme pour ÉS (DEV-421). Seules la
 * variable du fluide et son index sont retenues : une variable d'un autre
 * fluide n'ouvre jamais un graphique. Fluide null : aucune variable.
 */
export function variablesDetenuesUBordeaux(
    informations: InformationUBordeaux | null | undefined
): string[] {
    const fluide = fluideUBordeaux(informations)
    if (!fluide) return []
    const { variable } = FLUIDES_UBORDEAUX[fluide]
    const famille = [variable, `idx${variable}`]

    const servies = (informations?.data?._variables ?? [])
        .map((v) => (v as { type?: unknown } | null)?.type)
        .filter((type): type is string => typeof type === "string")

    const detenues =
        servies.length > 0
            ? servies
            : famille.filter((nom) =>
                  PAS_UBORDEAUX.some(({ grain }) =>
                      estUneBorne(
                          bornesDuPas(informations, nom, grain)?.date_from
                      )
                  )
              )

    return famille.filter((nom) => detenues.includes(nom))
}
